import {
  Button,
  Checkbox,
  DeleteIcon,
  DownloadIcon,
  KeyboardArrowDownIcon,
  LoadingButton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@firecms/ui";
import { useState } from "react";

import { CSV_DOWNLOAD_LABEL } from "../../PromoCodesList.constants";
import {
  downloadPromoCodesCsv,
  formatPromoCodeType,
  isoDateToLocaleDateString,
  isPromoCodeExpired,
} from "../../PromoCodesList.utils";
import PromoCodeStatusTag from "../PromoCodeStatusTag";
import { type PromoCodesTableProps } from "./PromoCodesTable.types";

const PromoCodesTable: React.FC<PromoCodesTableProps> = ({
  codes,
  total,
  onFetchMore,
  loading,
  loadingMore = false,
  deleteLoading,
  onDeletePromoCode,
}) => {
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);

  const handleSelectAllToggle = (checked: boolean): void => {
    setSelectedCodes(checked ? codes.map((promoCode) => promoCode.code) : []);
  };

  const handleDeleteSelected = (): void => {
    const documentIds = codes
      .filter((promoCode) => selectedCodes.includes(promoCode.code))
      .map((promoCode) => promoCode.documentId);

    if (
      !window.confirm(
        `Bist du sicher, dass du ${String(selectedCodes.length)} Promo Code${selectedCodes.length !== 1 ? "s" : ""} löschen möchtest?`,
      )
    ) {
      return;
    }

    void onDeletePromoCode({ documentIds }).then(() => {
      setSelectedCodes([]);
    });
  };

  const handleDeleteSingle = async (documentId: string, code: string): Promise<void> => {
    if (!window.confirm("Bist du sicher, dass du den Promo Code löschen möchtest?")) {
      return;
    }

    await onDeletePromoCode({ documentIds: [documentId] });
    setSelectedCodes((previousCodes) => previousCodes.filter((selectedCode) => selectedCode !== code));
  };

  if (codes.length === 0) {
    return <p className="text-sm text-gray-600 dark:text-gray-300">Keine Promo Codes gefunden.</p>;
  }

  const allSelected = selectedCodes.length === codes.length && codes.length > 0;
  const hasSelection = selectedCodes.length > 0;
  const codesForCsvExport = hasSelection ? codes.filter((promoCode) => selectedCodes.includes(promoCode.code)) : codes;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outlined"
          onClick={() => {
            downloadPromoCodesCsv(codesForCsvExport);
          }}
          disabled={loading || codesForCsvExport.length === 0}
          startIcon={<DownloadIcon size="small" />}
        >
          {hasSelection
            ? `${CSV_DOWNLOAD_LABEL} (${String(selectedCodes.length)})`
            : `${CSV_DOWNLOAD_LABEL} (aktuell sichtbare)`}
        </Button>
        {selectedCodes.length > 0 ? (
          <LoadingButton
            color="error"
            onClick={handleDeleteSelected}
            disabled={deleteLoading}
            loading={deleteLoading}
            startIcon={<DeleteIcon size="small" />}
          >
            Löschen ({selectedCodes.length})
          </LoadingButton>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <Table className="min-w-full">
          <TableHeader>
            <TableCell header>
              <Checkbox checked={allSelected} onCheckedChange={handleSelectAllToggle} size="small" />
            </TableCell>
            <TableCell header>Code</TableCell>
            <TableCell header>Typ</TableCell>
            <TableCell header>Monate</TableCell>
            <TableCell header>Erstellt am</TableCell>
            <TableCell header>Ablaufdatum</TableCell>
            <TableCell header>Abgelaufen</TableCell>
            <TableCell header>Eingelöst</TableCell>
            <TableCell header>Aktion</TableCell>
          </TableHeader>
          <TableBody>
            {codes.map((promoCode) => {
              const isExpired = isPromoCodeExpired(promoCode.expiresOnIsoDate);
              const isUsed = Boolean(promoCode.usedOnIsoDate);

              return (
                <TableRow key={promoCode.code}>
                  <TableCell>
                    <Checkbox
                      checked={selectedCodes.includes(promoCode.code)}
                      onCheckedChange={(checked) => {
                        setSelectedCodes((previousCodes) =>
                          checked
                            ? [...previousCodes, promoCode.code]
                            : previousCodes.filter((selectedCode) => selectedCode !== promoCode.code),
                        );
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{promoCode.code}</TableCell>
                  <TableCell>{formatPromoCodeType(promoCode.type)}</TableCell>
                  <TableCell>{promoCode.codeValue}</TableCell>
                  <TableCell>{isoDateToLocaleDateString(promoCode.createdOnIsoDate)}</TableCell>
                  <TableCell>{isoDateToLocaleDateString(promoCode.expiresOnIsoDate)}</TableCell>
                  <TableCell>
                    {isExpired ? (
                      <PromoCodeStatusTag type="danger" text="Abgelaufen" iconKey="close" />
                    ) : (
                      <PromoCodeStatusTag type="success" text="Aktiv" iconKey="check" />
                    )}
                  </TableCell>
                  <TableCell>
                    {isUsed ? (
                      <PromoCodeStatusTag type="info" text="Eingelöst" iconKey="done_all" />
                    ) : (
                      <PromoCodeStatusTag type="success" text="Verfügbar" iconKey="check" />
                    )}
                  </TableCell>
                  <TableCell>
                    <LoadingButton
                      color="error"
                      size="small"
                      onClick={async () => {
                        await handleDeleteSingle(promoCode.documentId, promoCode.code);
                      }}
                      disabled={deleteLoading}
                      loading={deleteLoading}
                      startIcon={<DeleteIcon size="small" />}
                    >
                      Löschen
                    </LoadingButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {codes.length < total && onFetchMore ? (
        <LoadingButton
          onClick={() => {
            onFetchMore();
          }}
          disabled={loading || loadingMore}
          loading={loadingMore}
          startIcon={<KeyboardArrowDownIcon size="small" />}
        >
          Mehr laden
        </LoadingButton>
      ) : null}
    </div>
  );
};

export default PromoCodesTable;
